import { disabledGrey } from '../app/theme';
import { IMAGE_PATHS } from './paths.constants';

export interface ImageFormat {
  path: string;
  alt: string;
}

export interface ReviewFormat {
  textQuestion: string;
  reviewImages: ImageFormat[];
  reviewCaptions?: string[];
}

export interface FindingsFormat {
  reviewText?: string;
  listElements?: string[];
  findingsImages: ImageFormat[];
  findingsCaptions?: string[];
}

export interface FindingsPanelFormat {
  layoutInColumns?: boolean;
  review: ReviewFormat;
  findings: FindingsFormat;
}

export interface FindingsDictionary {
  [module: string]: {
    [page: string]: FindingsPanelFormat;
  };
}

const FINDINGS: FindingsDictionary = {
  axuminInterpretation: {
    '12': {
      review: {
        textQuestion: 'Based on this image, where do you see abnormal uptake?',
        reviewImages: [IMAGE_PATHS.MILD_BLADDER_ACTIVITY, IMAGE_PATHS.SAGITTAL_VIEW_STEP_3],
        reviewCaptions: ['Cranial to caudal view', 'Sagittal view'],
      },
      findings: {
        findingsImages: [IMAGE_PATHS.MILD_BLADDER_ACTIVITY, IMAGE_PATHS.SAGITTAL_VIEW_STEP_3_ARROWS],
        findingsCaptions: ['Cranial to caudal view', 'Sagittal view'],
        listElements: [
          'No uptake at anastomotic region on sagittal view (yellow arrows)',
          'Cranial to caudal review demonstrated mild symmetric uptake in bladder wall',
          'Image interpretation: negative prostate bed',
        ],
      },
    },
    '13': {
      review: {
        textQuestion: `Determine the location of the lesion<br /><em style="color:${disabledGrey}">Subject presented with rising PSA (10.6 ng/mL; nadir 6.6 ng/mL), post-cryotherapy</em>`,
        reviewImages: [IMAGE_PATHS.STEP_3_CORONAL, IMAGE_PATHS.STEP_3_SAGITAL],
        reviewCaptions: ['Coronal view', 'Sagittal view'],
      },
      findings: {
        reviewText: '<em>Subject presented with rising PSA (10.6 ng/mL; nadir 6.6 ng/mL), post-cryotherapy</em>',
        findingsImages: [IMAGE_PATHS.STEP_3_CORONAL_ARROW, IMAGE_PATHS.STEP_3_SAGITAL_ARROW],
        findingsCaptions: ['Coronal view', 'Sagittal view'],
        listElements: [
          'Image interpretation: positive prostate (yellow arrows); negative extra-prostatic PSA decreased to 0.49 ng/mL post-salvage cryotherapy',
        ],
      },
    },
    '15': {
      layoutInColumns: true,
      review: {
        textQuestion: 'Determine the location of the lesions',
        reviewImages: [IMAGE_PATHS.AXIAL_VIEW, IMAGE_PATHS.CORONAL_VIEW_STEP_3, IMAGE_PATHS.REVIEW_AXIAL_2],
        reviewCaptions: ['Axial view', 'Coronal view', 'Axial view'],
      },
      findings: {
        findingsImages: [IMAGE_PATHS.FINDINGS_AXIAL_3, IMAGE_PATHS.FINDINGS_CORONAL, IMAGE_PATHS.FINDINGS_AXIAL_2],
        findingsCaptions: ['Axial view', 'Coronal view', 'Axial view'],
        listElements: [
          '1.5 x 1.2 cm right iliac bifurcation node, is suspicious for a metastasis (purple)',
          'Mild uptake in left external iliac, left obturator, or symmetric inguinal nodes (yellow) is likely benign',
          'Nodal dissection was malignant only where Axumin was hotter than marrow',
        ],
      },
    },
  },
  axuminAcquisition: {},
  poslumaAcquisition: {},
  poslumaInterpretation: {
    '12': {
      layoutInColumns: true,
      review: {
        textQuestion:
          '<strong>Based on this image, what should not be mistaken for abnormal uptake in lymph nodes?</strong>',
        reviewImages: [IMAGE_PATHS.PELVIC_GANGLIA_PET],
      },
      findings: {
        findingsImages: [IMAGE_PATHS.PELVIC_GANGLIA_PET_ARROWS],
        listElements: [
          'SUV scale is 0-10',
          'Physiological uptake of POSLUMA in sacral ganglion',
          'Not to be mistaken for a lymph node',
        ],
      },
    },
    '13': {
      layoutInColumns: true,
      review: {
        textQuestion: '<strong>Based on this image, where do you see abnormal uptake?</strong>',
        reviewImages: [IMAGE_PATHS.MEDIASTINAL_AND_HILAR_LYMPH_NODES],
      },
      findings: {
        findingsImages: [IMAGE_PATHS.MEDIASTINAL_AND_HILAR_LYMPH_NODES_ARROWS],
        findingsCaptions: [],
        listElements: [
          'Focal uptake in mediastinal (left images) and hilar (right images) lymph nodes is consistent with positive lesions',
        ],
      },
    },
    '14': {
      layoutInColumns: true,
      review: {
        textQuestion:
          '<strong>Based on this image, what should not be mistaken for abnormal uptake in lymph nodes?</strong>',
        reviewImages: [IMAGE_PATHS.LYMPTH_NODES],
      },
      findings: {
        findingsImages: [IMAGE_PATHS.LYMPTH_NODES_ARROWS],
        listElements: [
          'Physiological uptake of POSLUMA in bilateral axillary (arrows) and inguinal (circles) lymph nodes',
        ],
      },
    },
  },
};

export default FINDINGS;
